/**
 * Licença local (1 aparelho) — sem verificação periódica / sem plano.
 *
 * Seu NW (atalho local com vendedor.local.json) = gera chaves.
 * Instalador PC / APK = ativam com a chave (sem aba Licenças).
 * Link público = só agendamento.
 */

import { CARIOCA_SITE_OFICIAL } from './barbeariaSyncNuvem';
import { isAppLojaNativo, isModoClientePublico } from './barbeariaLogica';

const LS_DEVICE = 'carioca-device-id-v1';
const LS_LICENCA = 'carioca-licenca-v1';
const ARQUIVO_VENDEDOR = 'vendedor.local.json';

/** Senha da API — só o NW do vendedor usa para criar chaves. */
export const SENHA_ADMIN_LICENCA = 'CariocaLicenca2026';

type NwWin = Window & {
  nw?: {
    require: (m: string) => unknown;
    App?: { startPath?: string };
  };
};

export function isNwDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean((window as NwWin).nw);
  } catch {
    return false;
  }
}

/**
 * true só no seu atalho local (pasta nw-app com vendedor.local.json).
 * Instalador do cliente NÃO leva esse arquivo → sem aba Licenças.
 */
export function isNwDesktopVendedor(): boolean {
  if (!isNwDesktop()) return false;
  try {
    const nw = (window as NwWin).nw;
    if (!nw?.require) return false;
    const fs = nw.require('fs') as {
      existsSync: (p: string) => boolean;
      readFileSync: (p: string, enc: string) => string;
    };
    const path = nw.require('path') as {
      join: (...parts: string[]) => string;
      dirname: (p: string) => string;
    };
    const candidatos: string[] = [];
    const proc = (globalThis as { process?: { execPath?: string; cwd?: () => string } }).process;
    if (proc?.execPath) {
      candidatos.push(path.join(path.dirname(proc.execPath), ARQUIVO_VENDEDOR));
    }
    if (nw.App?.startPath) {
      candidatos.push(path.join(nw.App.startPath, ARQUIVO_VENDEDOR));
    }
    if (typeof proc?.cwd === 'function') {
      candidatos.push(path.join(proc.cwd(), ARQUIVO_VENDEDOR));
    }
    for (const p of candidatos) {
      if (!fs.existsSync(p)) continue;
      try {
        const data = JSON.parse(fs.readFileSync(p, 'utf8')) as { vendedor?: boolean };
        if (data?.vendedor === true) return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  } catch {
    return false;
  }
}

export type LicencaLocal = {
  chave: string;
  deviceId: string;
  ativadaEm: string;
  clienteNome?: string;
};

export function apiLicencasUrl(): string {
  try {
    const base = import.meta.env.VITE_CARIOCA_API_URL as string | undefined;
    if (base && String(base).trim()) {
      return String(base).replace(/\/$/, '') + '/api/licencas';
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (window.location.protocol === 'file:' || window.location.hostname === 'localhost') {
      return `${CARIOCA_SITE_OFICIAL}/api/licencas`;
    }
    // Capacitor / páginas
    if (window.location.hostname.includes('pages.dev') || window.location.protocol === 'https:') {
      return `${window.location.origin}/api/licencas`;
    }
  }
  return `${CARIOCA_SITE_OFICIAL}/api/licencas`;
}

export function obterDeviceId(): string {
  try {
    let id = localStorage.getItem(LS_DEVICE);
    if (id && id.length >= 8) return id;
    id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(LS_DEVICE, id);
    return id;
  } catch {
    return `dev-fallback-${Date.now()}`;
  }
}

export function lerLicencaLocal(): LicencaLocal | null {
  try {
    const raw = localStorage.getItem(LS_LICENCA);
    if (!raw) return null;
    const data = JSON.parse(raw) as LicencaLocal;
    if (!data?.chave || !data?.deviceId) return null;
    return data;
  } catch {
    return null;
  }
}

export function salvarLicencaLocal(lic: LicencaLocal): void {
  localStorage.setItem(LS_LICENCA, JSON.stringify(lic));
}

export function limparLicencaLocal(): void {
  try {
    localStorage.removeItem(LS_LICENCA);
  } catch {
    /* ignore */
  }
}

/**
 * Quem precisa digitar chave de ativação:
 * - APK (Capacitor) — mesmo que a URL seja localhost
 * - Instalador PC do cliente (NW sem vendedor.local.json)
 * Quem NÃO precisa:
 * - Seu NW local (vendedor), site público, Vite no navegador
 */
export function precisaLicencaLoja(): boolean {
  if (typeof window === 'undefined') return false;
  if (isNwDesktopVendedor()) return false;
  // APK / NW cliente: sempre pede chave (Capacitor usa hostname localhost!)
  if (isAppLojaNativo()) return true;
  // Navegador: só site público fica sem chave; Vite localhost = dev livre
  if (isModoClientePublico()) return false;
  return false;
}

export function textoWhatsAppChave(chave: string, clienteNome?: string): string {
  const nome = (clienteNome || '').trim();
  const saudacao = nome ? `Olá, ${nome}!` : 'Olá!';
  return (
    `${saudacao}\n\n` +
    `Segue sua chave de acesso do *Carioca Barbershop*:\n\n` +
    `*${chave}*\n\n` +
    `Instale o app, abra e digite a chave na tela de ativação.\n` +
    `A chave vale para *1 aparelho* apenas.`
  );
}

export function licencaLocalValida(): boolean {
  const lic = lerLicencaLocal();
  if (!lic) return false;
  const device = obterDeviceId();
  return lic.deviceId === device && Boolean(lic.chave);
}

export function formatarChaveDigitada(raw: string): string {
  const limpo = String(raw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  const comPrefixo = limpo.startsWith('CRCA') ? limpo : `CRCA${limpo}`;
  const corpo = comPrefixo.slice(4).slice(0, 12);
  const partes: string[] = [];
  for (let i = 0; i < corpo.length; i += 4) partes.push(corpo.slice(i, i + 4));
  return partes.length ? `CRCA-${partes.join('-')}` : 'CRCA-';
}

export async function ativarLicencaNaNuvem(
  chave: string
): Promise<{ ok: true; licenca: LicencaLocal } | { ok: false; erro: string }> {
  const deviceId = obterDeviceId();
  const chaveFmt = formatarChaveDigitada(chave);
  try {
    const res = await fetch(apiLicencasUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ acao: 'ativar', chave: chaveFmt, deviceId }),
    });
    const data = (await res.json()) as {
      ok?: boolean;
      erro?: string;
      chave?: string;
      deviceId?: string;
      ativadaEm?: string;
      clienteNome?: string;
    };
    if (!res.ok || !data.ok) {
      return { ok: false, erro: data.erro || 'Não foi possível ativar' };
    }
    const lic: LicencaLocal = {
      chave: data.chave || chaveFmt,
      deviceId: data.deviceId || deviceId,
      ativadaEm: data.ativadaEm || new Date().toISOString(),
      clienteNome: data.clienteNome || '',
    };
    salvarLicencaLocal(lic);
    return { ok: true, licenca: lic };
  } catch {
    return {
      ok: false,
      erro: 'Sem internet. Conecte uma vez para ativar (depois o app funciona offline).',
    };
  }
}

export type LicencaRemota = {
  chave: string;
  criadaEm: string;
  nota: string;
  clienteNome: string;
  deviceId: string | null;
  ativadaEm: string | null;
  revogada: boolean;
};

async function postAdmin(
  acao: string,
  adminSenha: string,
  extra: Record<string, unknown> = {}
): Promise<{ ok: boolean; erro?: string; [k: string]: unknown }> {
  const res = await fetch(apiLicencasUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Carioca-Admin': adminSenha,
    },
    body: JSON.stringify({ acao, adminSenha, ...extra }),
  });
  const data = (await res.json()) as { ok?: boolean; erro?: string; [k: string]: unknown };
  if (!res.ok || !data.ok) {
    return { ok: false, erro: data.erro || 'Falha na requisição' };
  }
  return { ok: true, ...data };
}

export async function listarLicencasAdmin(adminSenha: string) {
  return postAdmin('listar', adminSenha) as Promise<{
    ok: boolean;
    erro?: string;
    licencas?: LicencaRemota[];
  }>;
}

export async function criarLicencaAdmin(
  adminSenha: string,
  opts: { clienteNome?: string; nota?: string }
) {
  return postAdmin('criar', adminSenha, opts) as Promise<{
    ok: boolean;
    erro?: string;
    licenca?: LicencaRemota;
  }>;
}

export async function liberarLicencaAdmin(adminSenha: string, chave: string) {
  return postAdmin('liberar', adminSenha, { chave });
}

export async function revogarLicencaAdmin(adminSenha: string, chave: string) {
  return postAdmin('revogar', adminSenha, { chave });
}

/** Painel web desligado — chaves só pelo NW local do vendedor. */
export function isPainelLicencasUrl(): boolean {
  return false;
}
